import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { UserPlus, Check, X, Loader2, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import { AssignToSessionDialog } from './AssignToSessionDialog';

interface AccessRequest {
  id: string;
  message: string | null;
  status: string;
  created_at: string;
  requested_by: string;
  profiles?: {
    name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
}

interface AccessRequestsListProps {
  workspaceId: string;
  onRefresh?: () => void;
  onMessageUser?: (userId: string) => void;
}

export const AccessRequestsList = ({ workspaceId, onRefresh, onMessageUser }: AccessRequestsListProps) => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [resolvedRequests, setResolvedRequests] = useState<Map<string, { status: 'approved' | 'dismissed'; sessionCount?: number }>>(new Map());
  const [assignDialog, setAssignDialog] = useState<{
    open: boolean;
    requestId: string;
    userId: string;
    userName: string;
  }>({ open: false, requestId: '', userId: '', userName: '' });

  const loadRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('access_requests')
        .select(`
          id,
          message,
          status,
          created_at,
          requested_by,
          profiles:requested_by (
            name,
            email,
            avatar_url
          )
        `)
        .eq('workspace_id', workspaceId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests((data as any) || []);
    } catch (err) {
      console.error('Failed to load access requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [workspaceId]);

  const handleApproveWithSessions = (req: AccessRequest) => {
    const profile = req.profiles as any;
    setAssignDialog({
      open: true,
      requestId: req.id,
      userId: req.requested_by,
      userName: profile?.name || profile?.email || 'Unbekannt',
    });
  };

  const handleAssignComplete = async (assignedCount?: number) => {
    const requestId = assignDialog.requestId;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase
        .from('access_requests')
        .update({
          status: 'approved',
          resolved_by: user?.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      // Show inline success state
      setResolvedRequests((prev) => new Map(prev).set(requestId, { 
        status: 'approved', 
        sessionCount: assignedCount || 0 
      }));

      toast({
        title: 'Anfrage bestätigt & zugewiesen',
        description: `${assignDialog.userName} wurde zu ${assignedCount || 'den gewählten'} Entscheidung${(assignedCount || 0) > 1 ? 'en' : ''} hinzugefügt.`,
      });

      // Remove after animation
      setTimeout(() => {
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
        setResolvedRequests((prev) => { const next = new Map(prev); next.delete(requestId); return next; });
        onRefresh?.();
      }, 2000);
    } catch (err) {
      console.error('Failed to approve request:', err);
      toast({
        title: 'Fehler',
        description: 'Die Anfrage konnte nicht abgeschlossen werden.',
        variant: 'destructive',
      });
    }
  };

  const handleDismiss = async (requestId: string) => {
    setResolving(requestId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('access_requests')
        .update({
          status: 'dismissed',
          resolved_by: user?.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (error) throw error;

      // Show inline dismissed state
      setResolvedRequests((prev) => new Map(prev).set(requestId, { status: 'dismissed' }));

      toast({
        title: 'Anfrage abgelehnt',
        description: 'Die Anfrage wurde abgelehnt.',
      });

      setTimeout(() => {
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
        setResolvedRequests((prev) => { const next = new Map(prev); next.delete(requestId); return next; });
        onRefresh?.();
      }, 1500);
    } catch (err) {
      console.error('Failed to dismiss request:', err);
      toast({
        title: 'Fehler',
        description: 'Die Anfrage konnte nicht bearbeitet werden.',
        variant: 'destructive',
      });
    } finally {
      setResolving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (requests.length === 0) return null;

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Zugriffs-Anfragen</h3>
          <Badge variant="secondary" className="text-xs">{requests.length}</Badge>
        </div>

        <div className="space-y-2">
          {requests.map((req) => {
            const profile = req.profiles as any;
            const resolved = resolvedRequests.get(req.id);

            if (resolved) {
              return (
                <div
                  key={req.id}
                  className={`p-3 rounded-lg border space-y-1 transition-all duration-500 ${
                    resolved.status === 'approved' 
                      ? 'border-emerald-500/30 bg-emerald-500/10' 
                      : 'border-border/30 bg-muted/20 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {resolved.status === 'approved' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <X className="h-4 w-4 text-muted-foreground" />
                    )}
                    <p className="text-sm font-medium">
                      {profile?.name || profile?.email || 'Unbekannt'}
                    </p>
                  </div>
                  <p className={`text-xs pl-6 ${
                    resolved.status === 'approved' 
                      ? 'text-emerald-700 dark:text-emerald-300' 
                      : 'text-muted-foreground'
                  }`}>
                    {resolved.status === 'approved'
                      ? `✓ Zu ${resolved.sessionCount} Entscheidung${(resolved.sessionCount || 0) > 1 ? 'en' : ''} hinzugefügt`
                      : 'Anfrage abgelehnt'}
                  </p>
                </div>
              );
            }

            return (
              <div
                key={req.id}
                className="p-3 rounded-lg border border-border/50 bg-muted/30 space-y-2 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {profile?.name || profile?.email || 'Unbekannt'}
                    </p>
                    {profile?.name && (
                      <p className="text-xs text-muted-foreground">{profile.email}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                    {new Date(req.created_at).toLocaleDateString('de-DE')}
                  </span>
                </div>

                {req.message && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground bg-background rounded-md p-2">
                    <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <p>{req.message}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleApproveWithSessions(req)}
                    disabled={resolving === req.id}
                    className="flex-1 text-xs"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Bestätigen & Zuweisen
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onMessageUser?.(req.requested_by)}
                    className="text-xs text-muted-foreground hover:text-primary"
                    title="Nachricht senden"
                  >
                    <Send className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDismiss(req.id)}
                    disabled={resolving === req.id}
                    className="text-xs text-muted-foreground"
                  >
                    {resolving === req.id ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <X className="h-3 w-3 mr-1" />
                    )}
                    Ablehnen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AssignToSessionDialog
        open={assignDialog.open}
        onOpenChange={(open) => setAssignDialog((prev) => ({ ...prev, open }))}
        workspaceId={workspaceId}
        userId={assignDialog.userId}
        userName={assignDialog.userName}
        onComplete={handleAssignComplete}
      />
    </>
  );
};
